/**
 * B6 · NIVELES — hooks del camino y de la oferta.
 *
 * La AUTORIDAD es el servidor: aquí no se recalcula qué está desbloqueado. Si
 * el cliente lo hiciera, diría algo distinto en cuanto una de las dos
 * implementaciones cambiara — y el que se equivocaría a la vista del alumno
 * sería este. Lo único que se deriva en el cliente es el DIBUJO (qué conector
 * va sólido y cuál punteado), que sale del grafo que el servidor ya envía.
 */
import { createQuery, useQueryClient, type UseQueryResult } from "@tanstack/solid-query";
import type { StageStatus, TrackState } from "@space/shared";
import { useActiveOrg } from "@/features/academy/hooks/use-active-org";
import {
  dismissOffer,
  fetchMyOffers,
  fetchMyState,
  fetchTracks,
  type PendingOffer,
  progressionQueryKeys,
  type TracksResponse,
} from "./api";

/** Rutas PUBLICADAS de la academia activa (el alumno nunca ve borradores). */
export function useTracks(): UseQueryResult<TracksResponse> {
  const { activeOrgId } = useActiveOrg();
  return createQuery<TracksResponse>(() => ({
    queryKey: progressionQueryKeys.tracks(activeOrgId(), false),
    queryFn: async () => fetchTracks(activeOrgId()!),
    enabled: Boolean(activeOrgId()),
  }));
}

/** Estado del propio alumno en una ruta. */
export function useTrackState(trackId: () => string | undefined): UseQueryResult<TrackState> {
  const { activeOrgId } = useActiveOrg();
  return createQuery<TrackState>(() => ({
    queryKey: progressionQueryKeys.myState(activeOrgId(), trackId()),
    queryFn: async () => fetchMyState(activeOrgId()!, trackId()!),
    enabled: Boolean(activeOrgId()) && Boolean(trackId()),
  }));
}

/** Ofertas vigentes. Persisten hasta caducar o hasta que el alumno las descarte. */
export function useOffers(): {
  query: UseQueryResult<PendingOffer[]>;
  dismiss: (offerId: string) => Promise<void>;
} {
  const { activeOrgId } = useActiveOrg();
  const client = useQueryClient();
  const query = createQuery<PendingOffer[]>(() => ({
    queryKey: progressionQueryKeys.offers(activeOrgId()),
    queryFn: async () => fetchMyOffers(activeOrgId()!),
    enabled: Boolean(activeOrgId()),
  }));
  const dismiss = async (offerId: string): Promise<void> => {
    const org = activeOrgId();
    if (!org) {
      return;
    }
    await dismissOffer(org, offerId);
    await client.invalidateQueries({ queryKey: progressionQueryKeys.offers(org) });
  };
  return { query, dismiss };
}

/** Una fila del camino: la etapa más cómo se conecta con la anterior. */
export interface PathRow {
  stageKey: string;
  stageId: string;
  title: string;
  status: StageStatus;
  blockedBy?: string | null;
  progressPct: number;
  /** Lado de la serpiente (alterna; en móvil la amplitud se reduce por CSS). */
  side: "center" | "left" | "right";
  /**
   * Conector con la etapa anterior:
   *   `none` — no hay anterior;
   *   `hard` — prerrequisito sin alternativa;
   *   `soft` — hay otra vía (así se dibuja el salto de nivel: la interfaz no
   *            conoce el concepto «salto», solo pinta el grafo derivado);
   *   `done` — ya recorrido.
   */
  link: "none" | "hard" | "soft" | "done";
}

/** Estados que cuentan como «tramo ya recorrido» al dibujar el conector. */
const DONE: ReadonlySet<StageStatus> = new Set<StageStatus>(["completed", "certified"]);

/** Convierte el estado del servidor en filas dibujables. Puro y testeable. */
export function toPathRows(state: TrackState | undefined): PathRow[] {
  if (!state) {
    return [];
  }
  const ordered = [...state.stages].toSorted((a, b) =>
    a.position === b.position ? (a.stageKey < b.stageKey ? -1 : 1) : a.position - b.position,
  );
  return ordered.map((stage, index) => {
    const previous = index > 0 ? ordered[index - 1] : undefined;
    const incoming = state.edges.filter((e) => e.toStageKey === stage.stageKey);
    let link: PathRow["link"] = "none";
    if (previous) {
      if (DONE.has(previous.status)) {
        link = "done";
      } else if (incoming.length > 0 && incoming.every((e) => !e.hard)) {
        link = "soft";
      } else {
        link = incoming.length > 0 ? "hard" : "done";
      }
    }
    return {
      stageKey: stage.stageKey,
      stageId: stage.stageId,
      title: stage.title,
      status: stage.status,
      blockedBy: stage.blockedBy,
      progressPct: stage.progressPct,
      side: index === 0 ? "center" : index % 2 === 1 ? "left" : "right",
      link,
    };
  });
}
